import React, { useState } from 'react';
import {
    Grid, Box, Typography, Paper, Stack
} from '@mui/material';
import { Select, Form, Input, DatePicker } from 'antd';
import AntTable from '../../TableResizable';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import InputForm from "../../InputT";
import dayjs from 'dayjs';

const Tab1 = (props: {
    form?: any,
    glResultats: any[],
    listTypes: any[],
    listStatuts: any[],
    listProduits: any[],
    listGestionnaires: any[],
    listEtapes: any[],
    onTypeChange: (val: string) => void,
    statusLoading: boolean;
}) => {
    const [search, setSearch] = useState('');

    const resultatsFiltres = props.glResultats.filter((row: any) =>
        Object.values(row).some(value => String(value ?? '').toLowerCase().includes(search.toLowerCase()))
    );

    const formatMontant = (val: number) => new Intl.NumberFormat('fr-FR').format(val || 0);

    const columns: ColumnsType<any> = [
        {
            title: 'Date Demande', key: 'DATEDEMANDE', width: 120, align: 'center' as const,
            render: (row: any) => row.DATEDEMANDE && (
                <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1, fontWeight: 'bold' }}>
                    <span>{dayjs(row.DATEDEMANDE).format('DD/MM/YYYY')}</span>
                    <span style={{ fontSize: '0.7rem', color: '#666' }}>{dayjs(row.DATEDEMANDE).format('HH:mm')}</span>
                </Box>
            )
        },
        { title: 'N° Demande', key: 'NUMERODEMANDE', width: 120, render: (row: any) => <span style={{ whiteSpace: 'nowrap' }}>{row.NUMERODEMANDE}</span> },
        { title: 'N° Financement', key: 'NUMEROFINANCEMENT', width: 130, render: (row: any) => <span style={{ whiteSpace: 'nowrap' }}>{row.NUMEROFINANCEMENT}</span> },
        { title: 'Client', key: 'NOMCLIENT', width: 180, render: (row: any) => <span style={{ whiteSpace: 'nowrap' }}>{row.NOMCLIENT}</span> },
        { title: 'Type Financement', key: 'TYPEFINANCEMENT', width: 130, render: (row: any) => <span style={{ whiteSpace: 'nowrap' }}>{row.TYPEFINANCEMENT}</span> },
        { title: 'Code Produit', key: 'CODEPRODUIT', width: 110, render: (row: any) => <span style={{ whiteSpace: 'nowrap' }}>{row.CODEPRODUIT}</span> },
        {
            title: 'Montant Demandé', key: 'MONTANTDEMANDE', width: 130, align: 'right' as const,
            render: (row: any) => <span style={{ whiteSpace: 'nowrap', color: '#2A6596', fontWeight: 'bold' }}>{formatMontant(row.MONTANTDEMANDE)}</span>
        },
        {
            title: 'Montant Payé', key: 'MONTANTPAYE', width: 120, align: 'right' as const,
            render: (row: any) => <span style={{ whiteSpace: 'nowrap', color: 'green', fontWeight: 'bold' }}>{formatMontant(row.MONTANTPAYE)}</span>
        },
        {
            title: 'Montant Restant', key: 'MONTANTRESTANT', width: 130, align: 'right' as const,
            render: (row: any) => <span style={{ whiteSpace: 'nowrap', color: 'red', fontWeight: 'bold' }}>{formatMontant(row.MONTANTRESTANT)}</span>
        },
        {
            title: 'Statut', key: 'STATUT', width: 120, align: 'center' as const,
            render: (row: any) => <Box sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>{row.STATUT}</Box>
        },
        { title: 'Étape Actuelle', key: 'ETAPEACTUELLE', width: 130, render: (row: any) => <span style={{ whiteSpace: 'nowrap' }}>{row.ETAPEACTUELLE}</span> },
        { title: 'Prochaine Étape', key: 'PROCHAINEETAPE', width: 130, render: (row: any) => <span style={{ whiteSpace: 'nowrap', color: '#666' }}>{row.PROCHAINEETAPE}</span> },
        { title: 'Responsable', key: 'RESPONSABLE', width: 120, render: (row: any) => <span style={{ whiteSpace: 'nowrap' }}>{row.RESPONSABLE}</span> },
        {
            title: 'Priorité', key: 'PRIORITEACTION', width: 100, align: 'center' as const,
            render: (row: any) => <span style={{ color: row.PRIORITEACTION === 'HAUTE' ? 'red' : 'inherit', whiteSpace: 'nowrap' }}>{row.PRIORITEACTION}</span>
        },
        { title: 'Niveau Alerte', key: 'NIVEAUALERT', width: 110, render: (row: any) => <span style={{ whiteSpace: 'nowrap' }}>{row.NIVEAUALERT || row.NIVEAUALERTE}</span> },
        { title: 'Jours Retard', key: 'JOURSRETARD', width: 100, align: 'center' as const, render: (row: any) => <span>{row.JOURSRETARD}</span> },
        { title: 'Délai Max', key: 'DELAIMAXACTION', width: 90, align: 'center' as const, render: (row: any) => <span>{row.DELAIMAXACTION}</span> },
        { title: 'Agence', key: 'CODEAGENCE', width: 90, render: (row: any) => <span>{row.CODEAGENCE}</span> },
        { title: 'Gestionnaire', key: 'GESTIONNAIRE', width: 120, render: (row: any) => <span style={{ whiteSpace: 'nowrap' }}>{row.GESTIONNAIRE}</span> },
        { title: 'Action Recommandée', key: 'ACTIONRECOMMANDEE', width: 150, render: (row: any) => <span>{row.ACTIONRECOMMANDEE}</span> },
        { title: 'Documents Requis', key: 'DOCUMENTSREQUIS', width: 140, render: (row: any) => <span>{row.DOCUMENTSREQUIS}</span> },
        { title: 'Commentaire', key: 'COMMENTAIRETRAITEMENT', width: 160, render: (row: any) => <span>{row.COMMENTAIRETRAITEMENT}</span> },
    ];

    return (
        <div className='tabItemAuto'>
            {/* Observation 6 : Conservé en absolute selon instruction, mais élargi à 90% pour plus de confort */}
            <Grid container className='border' sx={{ width: "85%", position: "absolute", p: 2 }}>

                {/* TITRE UNIQUE POUR LE CONTENEUR GLOBAL */}
                <legend>PARAMETRES DE FILTRAGE DU JOURNAL</legend>

                {/* --- COLONNE GAUCHE : PÉRIODE & PRODUITS --- */}
                <Grid item xs={12} md={6} sx={{ pr: 2 }}>
                    <Form.Item
                        label="Type de financement"
                        name="TYPEFINANCEMENT"
                        labelCol={{ span: 7 }}
                        wrapperCol={{ span: 17 }}
                    >
                        <Select
                            placeholder="Sélectionner"
                            style={{ width: '100%' }}
                            onChange={props.onTypeChange}
                            options={props.listTypes.map(t => ({ value: t.CODE, label: t.LIBELLE }))}
                            allowClear
                        />
                    </Form.Item>

                    <Form.Item
                        label="Produit spécifique"
                        name="CODEPRODUIT"
                        labelCol={{ span: 7 }}
                        wrapperCol={{ span: 17 }}
                    >
                        <Select
                            placeholder="Sélectionner"
                            style={{ width: '100%' }}
                            disabled={props.listProduits.length === 0}
                            options={props.listProduits.map(p => ({ value: p.CODE, label: p.LIBELLE }))}
                            allowClear
                        />
                    </Form.Item>

                    <Form.Item
                        label="Gestionnaire"
                        name="GESTIONNAIRE"
                        labelCol={{ span: 7 }}
                        wrapperCol={{ span: 17 }}
                    >
                        <Select
                            placeholder="Sélectionner"
                            style={{ width: '100%' }}
                            disabled={props.listGestionnaires.length === 0}
                            options={props.listGestionnaires.map((g: any) => ({ value: g.CODE, label: g.LIBELLE }))}
                            allowClear
                        />
                    </Form.Item>
                </Grid>

                {/* --- COLONNE DROITE : PERSONNEL & ÉTATS --- */}
                <Grid item xs={12} md={6} className='border-left' sx={{ pl: 2 }}>
                    <Form.Item
                        label="Statut"
                        name="STATUT"
                        labelCol={{ span: 7 }}
                        wrapperCol={{ span: 17 }}
                    >
                        <Select
                            placeholder={props.glResultats.length > 0 ? "Filtrer par statut" : "Consultez d'abord pour voir les statuts"}
                            style={{ width: '100%' }}
                            loading={props.statusLoading}
                            disabled={props.glResultats.length === 0}
                            options={props.listStatuts.map(s => ({ value: s.CODE, label: s.LIBELLE }))}
                            allowClear
                        />
                    </Form.Item>

                    <Form.Item
                        label="Étape actuelle"
                        name="ETAPEACTUELLE"
                        labelCol={{ span: 7 }}
                        wrapperCol={{ span: 17 }}
                    >
                        <Select
                            placeholder="Sélectionner"
                            style={{ width: '100%' }}
                            disabled={props.listEtapes.length === 0}
                            options={props.listEtapes.map(e => ({ value: e.CODE, label: e.LIBELLE }))}
                            allowClear
                        />
                    </Form.Item>

                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={6}>
                            <Form.Item
                                label="Période Début"
                                name="DATEDEBUT"
                                labelCol={{ span: 7 }}
                                wrapperCol={{ span: 17 }}
                                getValueProps={(value: any) => ({ value: value ? dayjs(value) : null })}
                                getValueFromEvent={(date: any) => (date ? date.format('YYYY-MM-DD') : '')}
                            >
                                <DatePicker format="DD/MM/YYYY" placeholder="jj/mm/aaaa" style={{ width: '100%' }} />
                            </Form.Item>
                        </Grid>
                        <Grid item xs={6}>
                            <Form.Item
                                label="Période Fin"
                                name="DATEFIN"
                                labelCol={{ span: 7 }}
                                wrapperCol={{ span: 17 }}
                                getValueProps={(value: any) => ({ value: value ? dayjs(value) : null })}
                                getValueFromEvent={(date: any) => (date ? date.format('YYYY-MM-DD') : '')}
                            >
                                <DatePicker format="DD/MM/YYYY" placeholder="jj/mm/aaaa" style={{ width: '100%' }} />
                            </Form.Item>
                        </Grid>
                    </Grid>
                </Grid>

                {/* --- ZONE DE RÉSULTATS --- */}
                <Grid item xs={12} sx={{ mt: 1 }}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }}>
                            <Box>
                                <Typography variant="h6" fontWeight="bold" color="#04446e">Journal de Financement détaillé</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {resultatsFiltres.length} dossier(s)
                                </Typography>
                            </Box>
                            <Input size="small" allowClear prefix={<SearchOutlined />} placeholder="Rechercher..." value={search} onChange={event => setSearch(event.target.value)} style={{ width: 220 }} />
                        </Stack>
                        <Box sx={{
                            minHeight: '250px',
                            width: '100%',
                            '& .ant-table-cell': { whiteSpace: 'nowrap' },
                            '& .ant-table-content, & .ant-table-body': {
                                '&::-webkit-scrollbar': { height: '5px', width: '5px' },
                                '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1' },
                                '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: '10px' },
                                '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#2A6596' },
                            }
                        }}>
                            <AntTable
                                dataSource={resultatsFiltres}
                                columns={columns}
                                rowKey={(record: any, index?: number) => `${record.NUMEROFINANCEMENT || record.NUMERODEMANDE || '-'}-${index}`}
                                size="small"
                                pagination={false}
                                sticky={true}
                                scroll={{ x: 'max-content', y: 'calc(100vh - 460px)' }}
                                locale={{ emptyText: 'Aucune donnée trouvée.' }}
                            />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </div>
    );
};

export default Tab1;
